import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, ChevronUp, ChevronDown, GripVertical,
  Upload, Link2, Phone, Mail, Globe, Home, Search, X, Eye,
  MessageCircle, ImageIcon, BadgeCheck
} from "lucide-react";
import { ICON_MAP, ICON_CATEGORIES, getIcon } from "@/components/landing/shared";
import { toast } from "sonner";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERNAL_PAGES = [
  { value: "/", label: "Inicio" },
  { value: "/cita", label: "Reservar cita" },
  { value: "/mecanica", label: "Mecanica" },
  { value: "/chapa-y-pintura", label: "Chapa y pintura" },
  { value: "/recambios", label: "Recambios" },
  { value: "/vehiculos", label: "Vehiculos" },
  { value: "/contacto", label: "Contacto" },
  { value: "/financiacion", label: "Financiacion" },
  { value: "/tasacion", label: "Tasacion" },
  { value: "/quienes-somos", label: "Quienes somos" },
];

/* ═══════════════════════════════════════════
   ICON PICKER — Visual icon selector
   ═══════════════════════════════════════════ */
export function IconPicker({ value, onChange, onClose }) {
  const [search, setSearch] = useState("");
  const allNames = Object.keys(ICON_MAP);

  const filtered = useMemo(() => {
    if (!search) return null;
    const q = search.toLowerCase();
    return allNames.filter(n => n.includes(q));
  }, [search, allNames]);

  const cats = filtered ? { ["Resultados"]: filtered } : ICON_CATEGORIES;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <Input
          value={search} onChange={e => setSearch(e.target.value)}
          className="rounded-sm h-8 pl-8 border-slate-200 text-sm"
          placeholder="Buscar icono..." data-testid="icon-search"
        />
        {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5 text-slate-400" /></button>}
      </div>
      <div className="max-h-[300px] overflow-y-auto space-y-3 pr-1">
        {Object.entries(cats).map(([cat, icons]) => (
          <div key={cat}>
            <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{cat}</p>
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-1">
              {icons.map(name => {
                const Ic = ICON_MAP[name];
                if (!Ic) return null;
                const sel = value === name;
                return (
                  <button key={name} onClick={() => { onChange(name); if (onClose) onClose(); }}
                    className={`flex flex-col items-center gap-0.5 p-2 rounded-sm border transition-all text-center ${sel ? "border-red-500 bg-red-50 ring-1 ring-red-500" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"}`}
                    title={name} data-testid={`icon-option-${name}`}>
                    <Ic className={`w-4 h-4 ${sel ? "text-red-600" : "text-slate-600"}`} />
                    <span className="text-[7px] text-slate-400 truncate w-full">{name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {filtered && filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Sin resultados para "{search}"</p>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LINK SELECTOR — Guided destination picker
   ═══════════════════════════════════════════ */
export function LinkSelector({ value, onChange }) {
  const detectType = (v) => {
    if (!v) return "internal";
    if (v.startsWith("tel:")) return "phone";
    if (v.startsWith("mailto:")) return "email";
    if (v.includes("wa.me") || v.includes("whatsapp")) return "whatsapp";
    if (v.startsWith("http://") || v.startsWith("https://")) return "external";
    return "internal";
  };
  const [linkType, setLinkType] = useState(() => detectType(value));

  const types = [
    { key: "internal", label: "Pagina interna", icon: Home },
    { key: "external", label: "URL externa", icon: Globe },
    { key: "phone", label: "Telefono", icon: Phone },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { key: "email", label: "Email", icon: Mail },
  ];

  const handleTypeChange = (type) => {
    setLinkType(type);
    if (type === "whatsapp") onChange("https://wa.me/34");
    else if (type === "phone") onChange("tel:");
    else if (type === "email") onChange("mailto:");
    else if (type === "external") onChange("https://");
    else onChange("/");
  };

  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Tipo de destino</Label>
      <div className="flex flex-wrap gap-1">
        {types.map(t => (
          <button key={t.key} onClick={() => handleTypeChange(t.key)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider border transition-all ${linkType === t.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
            data-testid={`link-type-${t.key}`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {linkType === "internal" && (
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Pagina de destino</Label>
          <div className="grid grid-cols-2 gap-1">
            {INTERNAL_PAGES.map(p => (
              <button key={p.value} onClick={() => onChange(p.value)}
                className={`text-left px-2.5 py-1.5 rounded-sm text-xs border transition-all ${value === p.value ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                data-testid={`internal-page-${p.value.replace(/\//g, "-")}`}>
                {p.label} <span className="text-slate-400 text-[9px] ml-1">{p.value}</span>
              </button>
            ))}
          </div>
          <Input value={value || ""} onChange={e => onChange(e.target.value)}
            className="rounded-sm h-8 border-slate-200 text-sm mt-1"
            placeholder="O escribe una ruta personalizada: /mi-pagina" data-testid="link-custom-input" />
        </div>
      )}

      {linkType === "external" && (
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">URL completa</Label>
          <Input value={value || ""} onChange={e => onChange(e.target.value)}
            className="rounded-sm h-8 border-slate-200 text-sm"
            placeholder="https://www.ejemplo.com" data-testid="link-external-input" />
        </div>
      )}

      {linkType === "phone" && (
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Numero de telefono</Label>
          <Input value={(value || "").replace("tel:", "")}
            onChange={e => onChange(`tel:${e.target.value.replace(/[^0-9+]/g, "")}`)}
            className="rounded-sm h-8 border-slate-200 text-sm"
            placeholder="916003800" data-testid="link-phone-input" />
          <p className="text-[9px] text-slate-400">Se generara el enlace tel: automaticamente</p>
        </div>
      )}

      {linkType === "whatsapp" && (
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Numero WhatsApp (con prefijo pais)</Label>
          <Input value={(value || "").replace("https://wa.me/", "")}
            onChange={e => onChange(`https://wa.me/${e.target.value.replace(/[^0-9]/g, "")}`)}
            className="rounded-sm h-8 border-slate-200 text-sm"
            placeholder="34667440816" data-testid="link-whatsapp-input" />
          <p className="text-[9px] text-slate-400">Usa el numero con codigo de pais sin +. Ej: 34667440816</p>
        </div>
      )}

      {linkType === "email" && (
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Direccion de email</Label>
          <Input value={(value || "").replace("mailto:", "")}
            onChange={e => onChange(`mailto:${e.target.value}`)}
            className="rounded-sm h-8 border-slate-200 text-sm"
            placeholder="info@ascauto.com" data-testid="link-email-input" />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   IMAGE UPLOAD FIELD — URL or Upload + preview
   ═══════════════════════════════════════════ */
export function ImageUploadField({ value, onChange, label, hint }) {
  const [uploading, setUploading] = useState(false);
  const isUploaded = (value || "").startsWith("/api/branding/gallery/");
  const [mode, setMode] = useState(isUploaded ? "upload" : "url");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) { toast.error("Formato no permitido. Usa JPG, PNG o WebP."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Maximo 10MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/branding/upload-gallery`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir imagen"); }
    setUploading(false);
  };

  const resolveUrl = (v) => getImageUrl(v);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label || "Imagen"}</Label>
        <div className="flex gap-1">
          <button onClick={() => setMode("url")} className={`text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${mode === "url" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500"}`} data-testid="img-mode-url">URL</button>
          <button onClick={() => setMode("upload")} className={`text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${mode === "upload" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500"}`} data-testid="img-mode-upload">Subir</button>
        </div>
      </div>

      {mode === "url" && (
        <Input value={isUploaded ? "" : (value || "")} onChange={e => onChange(e.target.value)}
          className="rounded-sm h-8 border-slate-200 text-sm"
          placeholder="https://..." data-testid="img-url-input" />
      )}

      {mode === "upload" && (
        <div className="border-2 border-dashed border-slate-300 rounded-sm p-3 text-center hover:border-slate-400 transition-colors">
          <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handleUpload} className="hidden" id={`img-upload-${label}`} data-testid="img-file-input" />
          <label htmlFor={`img-upload-${label}`} className="cursor-pointer flex flex-col items-center gap-1">
            <Upload className="w-5 h-5 text-slate-400" />
            <span className="text-[10px] text-slate-500">{uploading ? "Subiendo..." : "Haz clic para subir"}</span>
            <span className="text-[9px] text-slate-400">JPG, PNG, WebP. Max 10MB</span>
          </label>
        </div>
      )}

      {value && (
        <div className="space-y-1">
          <div className="relative border border-slate-200 rounded-sm overflow-hidden bg-slate-50 h-32">
            <img src={resolveUrl(value)} alt="preview" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
            <button onClick={() => onChange("")}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-sm p-0.5 hover:bg-red-700">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          {isUploaded && <div className="flex items-center gap-1 text-[9px] text-green-700"><BadgeCheck className="w-3 h-3" /> Imagen subida</div>}
        </div>
      )}
      {hint && <p className="text-[9px] text-slate-400">{hint}</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════
   QUICK ACCESS EDITOR — Full editor modal
   ═══════════════════════════════════════════ */
export function QuickAccessEditor({ items, setItems }) {
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({ title: "", icon: "calendar", link: "/cita", primary: false, icon_mode: "icon", image_url: "" });

  const openNew = () => {
    setEditIdx(-1);
    setForm({ title: "", icon: "calendar", link: "/cita", primary: false, icon_mode: "icon", image_url: "" });
  };
  const openEdit = (i) => {
    const it = items[i];
    const icon_mode = it.image_url ? "image" : "icon";
    setEditIdx(i);
    setForm({ ...it, icon_mode });
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const moveUp = (i) => { if (!i) return; const a = [...items]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setItems(a); };
  const moveDown = (i) => { if (i >= items.length - 1) return; const a = [...items]; [a[i], a[i + 1]] = [a[i + 1], a[i]]; setItems(a); };

  const resolveUrl = (v) => getImageUrl(v);

  const save = () => {
    if (!form.title) { toast.error("El titulo es obligatorio"); return; }
    if (form.icon_mode === "icon" && !form.icon) { toast.error("Selecciona un icono"); return; }
    if (form.icon_mode === "image" && !form.image_url) { toast.error("Sube una imagen"); return; }
    if (!form.link) { toast.error("Selecciona un destino"); return; }
    const clean = { title: form.title, icon: form.icon || "calendar", link: form.link, primary: !!form.primary, image_url: form.icon_mode === "image" ? form.image_url : "" };
    if (editIdx === -1) setItems([...items, clean]);
    else setItems(items.map((it, i) => i === editIdx ? clean : it));
    setEditIdx(null);
  };

  const PreviewIcon = form.icon_mode === "image" && form.image_url
    ? () => <img src={resolveUrl(form.image_url)} alt="" className="w-5 h-5 object-cover rounded-sm" />
    : getIcon(form.icon);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">Accesos Rapidos</h4>
        <Button variant="outline" size="sm" onClick={openNew} className="rounded-sm text-[10px] h-6 px-2 uppercase tracking-wider" data-testid="add-quick-access"><Plus className="w-3 h-3 mr-1" /> Nuevo</Button>
      </div>
      <div className="space-y-1">
        {items.map((it, i) => {
          const Ic = it.image_url ? null : getIcon(it.icon);
          return (
            <div key={i} className={`flex items-center gap-2 p-2 border rounded-sm text-xs group ${it.primary ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`qa-item-${i}`}>
              <GripVertical className="w-3 h-3 text-slate-300 flex-shrink-0" />
              <div className="w-7 h-7 flex items-center justify-center bg-slate-100 rounded-sm flex-shrink-0">
                {it.image_url ? <img src={resolveUrl(it.image_url)} alt="" className="w-full h-full object-cover rounded-sm" /> : Ic && <Ic className="w-3.5 h-3.5 text-slate-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-slate-900 truncate block">{it.title}</span>
                <span className="text-slate-400 truncate block text-[10px]">{it.link}</span>
              </div>
              {it.primary && <span className="text-[8px] uppercase tracking-wider font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-sm">Principal</span>}
              <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="sm" onClick={() => moveUp(i)} disabled={!i} className="h-6 w-6 p-0"><ChevronUp className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => moveDown(i)} disabled={i >= items.length - 1} className="h-6 w-6 p-0"><ChevronDown className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(i)} className="h-6 w-6 p-0"><Pencil className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(i)} className="h-6 w-6 p-0 text-red-500"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={editIdx !== null} onOpenChange={() => setEditIdx(null)}>
        <DialogContent className="max-w-xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="heading-industrial text-sm uppercase">{editIdx === -1 ? "Nuevo acceso rapido" : "Editar acceso rapido"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">

            {/* LIVE PREVIEW */}
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</Label>
              <div className={`flex flex-col items-center justify-center gap-2 p-5 text-center shadow-md ${form.primary ? "bg-red-600 text-white" : "bg-white text-slate-900 border border-slate-200"}`}>
                <div className={`w-10 h-10 flex items-center justify-center ${form.primary ? "bg-white/20" : "bg-slate-900/5"}`}>
                  <PreviewIcon className={`w-4 h-4 ${form.primary ? "text-white" : "text-slate-700"}`} />
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${form.primary ? "text-white" : "text-slate-800"}`}>{form.title || "Titulo"}</span>
              </div>
            </div>

            {/* TITLE */}
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Titulo</Label>
              <Input value={form.title || ""} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="rounded-sm h-8 border-slate-200 text-sm" placeholder="Ej: Pedir cita" data-testid="qa-title-input" />
            </div>

            {/* ICON MODE */}
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Representacion visual</Label>
              <div className="flex gap-2">
                <button onClick={() => setForm(p => ({ ...p, icon_mode: "icon" }))}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-all ${form.icon_mode === "icon" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
                  data-testid="qa-mode-icon">
                  <Search className="w-3.5 h-3.5" /> Usar icono
                </button>
                <button onClick={() => setForm(p => ({ ...p, icon_mode: "image" }))}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-all ${form.icon_mode === "image" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
                  data-testid="qa-mode-image">
                  <ImageIcon className="w-3.5 h-3.5" /> Usar imagen
                </button>
              </div>
            </div>

            {/* ICON PICKER */}
            {form.icon_mode === "icon" && (
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Icono seleccionado: <span className="text-red-600">{form.icon || "ninguno"}</span></Label>
                <IconPicker value={form.icon} onChange={v => setForm(p => ({ ...p, icon: v }))} />
              </div>
            )}

            {/* IMAGE UPLOAD */}
            {form.icon_mode === "image" && (
              <ImageUploadField
                value={form.image_url}
                onChange={v => setForm(p => ({ ...p, image_url: v }))}
                label="Imagen del acceso"
                hint="Se usara como icono del boton"
              />
            )}

            {/* LINK SELECTOR */}
            <div className="pt-3 border-t border-slate-100">
              <LinkSelector value={form.link} onChange={v => setForm(p => ({ ...p, link: v }))} />
            </div>

            {/* PRIMARY */}
            <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
              <input type="checkbox" checked={!!form.primary} onChange={e => setForm(p => ({ ...p, primary: e.target.checked }))}
                className="rounded-sm border-slate-300" id="qa-primary" data-testid="qa-primary-toggle" />
              <label htmlFor="qa-primary" className="text-xs font-semibold text-slate-700">Destacado (fondo rojo)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIdx(null)} className="rounded-sm text-xs uppercase h-8">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold h-8" data-testid="qa-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════
   ICON ITEM EDITOR — For services/advantages
   with visual icon picker instead of text field
   ═══════════════════════════════════════════ */
export function IconItemEditor({ items, setItems, fields, title, icon: TitleIcon, compact }) {
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({});
  const [showIconPicker, setShowIconPicker] = useState(false);

  const openEdit = (i) => { setEditIdx(i); setForm({ ...items[i] }); setShowIconPicker(false); };
  const openNew = () => { setEditIdx(-1); setForm(fields.reduce((a, f) => ({ ...a, [f.key]: f.default ?? "" }), { order: items.length + 1 })); setShowIconPicker(false); };
  const save = () => { if (editIdx === -1) setItems([...items, form]); else setItems(items.map((it, i) => i === editIdx ? form : it)); setEditIdx(null); };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const moveUp = (i) => { if (!i) return; const a = [...items]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setItems(a.map((x, idx) => ({ ...x, order: idx + 1 }))); };
  const moveDown = (i) => { if (i >= items.length - 1) return; const a = [...items]; [a[i], a[i + 1]] = [a[i + 1], a[i]]; setItems(a.map((x, idx) => ({ ...x, order: idx + 1 }))); };

  const hasIcon = fields.some(f => f.key === "icon");

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">{TitleIcon && <TitleIcon className="w-3.5 h-3.5 text-red-600" />}{title}</h4>
        <Button variant="outline" size="sm" onClick={openNew} className="rounded-sm text-[10px] h-6 px-2 uppercase tracking-wider" data-testid={`add-${title.toLowerCase().replace(/\s/g, "-")}`}><Plus className="w-3 h-3 mr-1" /> Nuevo</Button>
      </div>
      <div className={compact ? "space-y-1" : "space-y-1.5"}>
        {items.map((it, i) => {
          const Ic = hasIcon ? getIcon(it.icon) : null;
          return (
            <div key={i} className="flex items-center gap-1.5 p-2 border border-slate-200 rounded-sm bg-white text-xs group">
              <GripVertical className="w-3 h-3 text-slate-300 flex-shrink-0" />
              {Ic && <div className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-sm flex-shrink-0"><Ic className="w-3 h-3 text-slate-600" /></div>}
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-slate-900 truncate block">{it.title || it.name || it.value || it.label || `#${i + 1}`}</span>
                {!compact && <span className="text-slate-400 truncate block text-[10px]">{it.description || it.text || ""}</span>}
              </div>
              <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="sm" onClick={() => moveUp(i)} disabled={!i} className="h-6 w-6 p-0"><ChevronUp className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => moveDown(i)} disabled={i >= items.length - 1} className="h-6 w-6 p-0"><ChevronDown className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(i)} className="h-6 w-6 p-0"><Pencil className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(i)} className="h-6 w-6 p-0 text-red-500"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={editIdx !== null} onOpenChange={() => setEditIdx(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="heading-industrial text-sm uppercase">{editIdx === -1 ? "Nuevo" : "Editar"} {title}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {fields.map(f => {
              if (f.key === "icon") {
                const CurIcon = getIcon(form.icon);
                return (
                  <div key={f.key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Icono</Label>
                      <div className="flex items-center gap-2">
                        {form.icon && <div className="flex items-center gap-1 text-xs text-slate-600"><CurIcon className="w-4 h-4 text-red-600" /><span className="text-[10px]">{form.icon}</span></div>}
                        <button onClick={() => setShowIconPicker(!showIconPicker)}
                          className="text-[10px] uppercase tracking-wider font-semibold text-red-600 hover:text-red-700"
                          data-testid="toggle-icon-picker">
                          {showIconPicker ? "Cerrar" : "Seleccionar"}
                        </button>
                      </div>
                    </div>
                    {showIconPicker && <IconPicker value={form.icon} onChange={v => { setForm(p => ({ ...p, icon: v })); setShowIconPicker(false); }} />}
                  </div>
                );
              }
              return (
                <div key={f.key} className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{f.label}</Label>
                  {f.type === "textarea" ? <textarea value={form[f.key] || ""} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className="flex min-h-[60px] w-full rounded-sm border border-slate-200 bg-transparent px-3 py-2 text-sm" rows={3} />
                    : f.type === "number" ? <Input type="number" value={form[f.key] ?? 0} onChange={e => setForm(p => ({ ...p, [f.key]: parseInt(e.target.value) || 0 }))} className="rounded-sm h-8 border-slate-200 text-sm" />
                    : f.type === "switch" ? <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.checked }))} className="rounded-sm border-slate-300" />
                    : <Input value={form[f.key] || ""} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className="rounded-sm h-8 border-slate-200 text-sm" placeholder={f.placeholder || ""} />}
                  {f.hint && <p className="text-[9px] text-slate-400">{f.hint}</p>}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIdx(null)} className="rounded-sm text-xs uppercase h-8">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold h-8">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
